import React from 'react';

// Agent output sometimes arrives as a JSON envelope (or a truncated one) rather
// than plain markdown. Pull content_markdown out when that happens.
export function extractMarkdown(raw) {
  if (!raw) return '';
  if (!raw.includes('"content_markdown"')) return raw;
  const text = raw
    .replace(/^```(?:json)?\s*\n?/i, '')
    .replace(/\n?```\s*$/i, '')
    .trim();
  const jsonStart = text.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const data = JSON.parse(text.slice(jsonStart));
      if (data && data.content_markdown) return data.content_markdown;
    } catch (e) { /* truncated */ }
  }
  const m = text.match(/"content_markdown"\s*:\s*"((?:[^"\\]|\\[\s\S])*)/);
  if (m && m[1]) {
    try {
      return JSON.parse('"' + m[1].replace(/\\$/, '') + '"');
    } catch (e2) {
      return m[1]
        .replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t')
        .replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    }
  }
  return raw;
}

export const phaseName = (p) => ({
  intake: 'Сбор данных', research: 'Исследование', evaluation: 'Оценка',
  architecture: 'Архитектура', critique: 'Критика',
  repair_cycle: 'Доработка', finalization: 'Финализация',
}[p] || p);

export const agentLabel = (name) => ({
  orchestrator: 'Оркестратор', researcher: 'Исследователь',
  osint_evaluator: 'OSINT-оценщик (легаси)', source_verifier: 'Верификатор источников',
  system_architect: 'Архитектор',
  analytical_reviewer: 'Аналитический ревьюер', editorial_reviewer: 'Редакционный ревьюер', technical_writer: 'Технический писатель',
  dev_team_lead: 'Dev Team Lead', dev_architect: 'Dev Архитектор',
  developer: 'Разработчик', dev_task_planner: 'Планировщик разработки',
  experimenter: 'Экспериментатор', criteria_evaluator: 'Оценщик критериев',
  analytics_lead: 'Внутренняя аналитика', research_lead: 'Исследование',
  dev_lead: 'Разработка', quality_lead: 'Качество', editorial_lead: 'Документация',
  software_lead: 'ПО', hardware_lead: 'Железо',
  document_analyst: 'Аналитик документов', interview_analyst: 'Аналитик интервью',
  stakeholder_analyst: 'Аналитик стейкхолдеров', gap_analyst: 'Аналитик пробелов',
  hypothesis_analyst: 'Аналитик гипотез', risk_analyst: 'Аналитик рисков',
  spec_writer: 'Автор ТЗ', presentation_designer: 'Дизайнер презентаций',
  component_analyst: 'Аналитик компонентов', schematic_designer: 'Схемотехник',
  model_designer: 'Конструктор моделей',
  skill_runner: 'Методика',
  business_lead: 'Бизнес-анализ', market_lead: 'Рыночная аналитика',
  economics_lead: 'Экономика',
  market_sizing_analyst: 'Аналитик объёма рынка',
  competition_analyst: 'Аналитик конкурентов',
  trend_analyst: 'Аналитик трендов',
  patent_analyst: 'Патентный аналитик',
  market_evidence_evaluator: 'Верификатор данных',
  monetization_strategist: 'Стратег монетизации',
  unit_economics_analyst: 'Аналитик юнит-экономики',
  financial_modeler: 'Финансовый аналитик',
  investment_analyst: 'Инвестиционный аналитик',
  portfolio_analyst: 'Портфельный аналитик',
  human: 'Пользователь', system: 'Система',
}[name] || name);

// Виды флагов наблюдателя. Закрытый набор с проверяемым определением у
// каждого: наблюдатель, свободный придумать категорию, порождает ленту,
// которую никто не читает.
export const flagKindLabel = (kind) => ({
  loop: 'Цикл',
  scope_drift: 'Уход от цели',
  suspicious_command: 'Подозрительная команда',
  contradiction: 'Противоречие',
}[kind] || kind);

export const flagSeverityColor = (severity) => ({
  high: 'var(--danger)', medium: 'var(--warning)', low: 'var(--text-muted)',
}[severity] || 'var(--warning)');

export const skillKindLabel = (kind) => ({
  document: 'Документ', deck: 'Презентация', scorecard: 'Критерии',
  method: 'Методика анализа', model: 'Расчётная модель',
}[kind] || kind);

export const skillStatusLabel = (status) => ({
  pending: 'ожидает', running: 'выполняется', done: 'готово',
  failed: 'не удалось', skipped: 'пропущено',
}[status] || status);

// An artifact produced by a skill is identified by the methodology, not by
// its technical type: five skills share the type `skill_document`, so showing
// the type alone would render them all identically.
export const artifactLabel = (artifact) =>
  artifact?.skill_slug || artifact?.artifact_type || '';

export const isAttemptLimit = reason => reason === 'Достигнут лимит попыток';

export const statusLabel = (status) => ({
  running: 'В работе', finished: 'Завершена', completed: 'Завершено', failed: 'Ошибка',
  waiting: 'Ожидание', waiting_tool: 'Вычисление', waiting_for_input: 'Нужен ответ', waiting_for_human: 'Нужен ваш ответ', created: 'Создано', paused: 'На паузе',
  cancelled: 'Отменено', todo: 'Запланировано', done: 'Готово', blocked: 'Заблокировано', stopped: 'Остановлена',
  pending: 'Ожидает ответа', answered: 'Ответ получен', active: 'Активен', inactive: 'Неактивен',
  skipped: 'Пропущено', archived: 'В архиве', expired: 'Срок ответа истёк', accepted: 'Принято', review: 'На проверке', budget_exhausted: 'Лимит исчерпан',
}[status] || status);

export function StatusBadge({ status }) {
  return <span className={`badge badge-${status}`} title={status}>{statusLabel(status)}</span>;
}

export const statusBadge = (status) => <StatusBadge status={status} />;
